// src/team/PaymentForm.tsx: the payment form inside Settings (docs/SPEC.md 16.6; docs/DECISIONS.md "Billing"). Stripe's
// Payment Element (bank account first, card second; the bank verified through the bank's own sign-in, or by small
// deposits), styled as our own fields (src/lib/stripe.ts#appearance), behind a SetupIntent the server makes for this
// form. The bank debit authorization Stripe requires shows under the bank fields; the Payment Element writes it.
//
// The billing address (sales tax needs it) is one line with Edit when it is known already (the one saved with the last
// method, else the builder's office): Edit opens Stripe's Address Element, filled in with it. When none is known the
// Address Element shows at once. Either way the address goes with the confirmation (the Payment Element never asks for
// it), so it is the payment method's billing address.
//
// The parent owns the button: it gets a handle whose confirm() checks the fields, confirms the SetupIntent and answers
// its id, which the parent hands to POST /api/team/billing/setup/complete. Stripe.js loads only when a form opens.
// Root class: bill-form.
import { useEffect, useRef, useState } from "preact/hooks";
import type { BillingAddress, BillingSetupPurpose, BillingSetupResponse } from "../../shared/api";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import { appearance, fonts, loadStripe, type StripeAddress, type StripeElement, type StripeElements } from "../lib/stripe";
import { me, tenant } from "../state/session";
import { Button, LinkButton, Skeleton } from "../ui";

export type ConfirmResult = { ok: true; setupIntentId: string } | { ok: false; message: string };
/** `mark` hears "submit" and "confirm" as each finishes, so a slow run can be read (BillingStep logs them). */
export type PaymentFormHandle = { confirm(mark?: (step: "submit" | "confirm") => void): Promise<ConfirmResult> };

/** Where Stripe sends the browser back if a bank or card check ever needs a full-page step (BillingSection reads it). */
export const RETURN_PATH = "/team/settings?billing=return";

const PHONE_FIELDS = "(max-width: 1023px), (pointer: coarse)";
const TRY_AGAIN = "That didn't go through. Check the details and try again.";

function toAddress(a: StripeAddress): BillingAddress {
  return { line1: a.line1, line2: a.line2 ?? null, city: a.city, state: a.state, postal_code: a.postal_code, country: a.country };
}

function toStripe(a: BillingAddress): StripeAddress {
  return { line1: a.line1, line2: a.line2 ?? null, city: a.city, state: a.state, postal_code: a.postal_code, country: a.country };
}

/** "12 Main St, Suite A, Springfield, NY 12345". */
export function addressLine(a: BillingAddress): string {
  return [a.line1, a.line2, a.city, [a.state, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

type Phase = "starting" | "mounting" | "ready" | "failed";

export function PaymentForm(props: {
  purpose: BillingSetupPurpose;
  onHandle: (h: PaymentFormHandle | null) => void;
  /** The address to start from (one line with Edit); null: the address fields show at once. */
  address?: BillingAddress | null;
  /** The billing address while it is being edited, once it is complete (null while it isn't): today's sales tax. */
  onAddress?: (a: BillingAddress | null) => void;
  /** Whether the payment fields are filled in (Stripe's own check), for a button that waits for them. */
  onComplete?: (complete: boolean) => void;
  /** Which kind of payment the fields hold now: "card" or "us_bank_account" (the button names its stages with it). */
  onMethod?: (type: string | null) => void;
  /**
   * The quote page (src/quote): its own route for the form's SetupIntent instead of the team's, the company the address is
   * for (there is no signed-in builder), and the person, whose name and email the page asks above the payment fields: Stripe's
   * own name and email fields are left out and the person's go with the confirmation.
   */
  open?: () => Promise<BillingSetupResponse>;
  company?: string;
  contact?: () => { name: string; email: string };
}) {
  const payRef = useRef<HTMLDivElement>(null);
  const addrRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const [phase, setPhase] = useState<Phase>("starting");
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const known = props.address ?? null;
  const [editing, setEditing] = useState(!known);
  // The address that goes with the confirmation: the known one, or what the address fields hold.
  const current = useRef<StripeAddress | null>(known ? toStripe(known) : null);
  const onHandle = useRef(props.onHandle);
  onHandle.current = props.onHandle;
  const onAddress = useRef(props.onAddress);
  onAddress.current = props.onAddress;
  const onComplete = useRef(props.onComplete);
  onComplete.current = props.onComplete;
  const onMethod = useRef(props.onMethod);
  onMethod.current = props.onMethod;

  useEffect(() => {
    let alive = true;
    let mounted: StripeElement[] = [];
    setPhase("starting");
    setFailure(null);
    const fail = (message: string | null) => {
      if (!alive) return;
      setFailure(message);
      setPhase("failed");
      onHandle.current(null);
    };
    void (async () => {
      try {
        const form = props.open ? await props.open() : props.purpose === "start" ? await api.billingSetup() : await api.billingPaymentMethod();
        const stripe = await loadStripe(form.publishableKey);
        if (!alive) return;
        setPhase("mounting");
        const phone = typeof window.matchMedia === "function" && window.matchMedia(PHONE_FIELDS).matches;
        const elements = stripe.elements({ clientSecret: form.clientSecret, appearance: appearance(phone), fonts: fonts(), loader: "auto" });
        elementsRef.current = elements;
        const name = props.company ?? tenant.value?.name ?? "";
        const email = props.contact ? "" : (me.value?.user?.email ?? "");
        const payment = elements.create("payment", {
          layout: { type: "tabs" },
          paymentMethodOrder: ["us_bank_account", "card"],
          // No Apple Pay or Google Pay (the Permissions-Policy keeps payment off); the authorization text for the bank
          // debit (and for keeping a card on file) shows, as Stripe requires. The address is the form's, sent on confirm.
          wallets: { applePay: "never", googlePay: "never" },
          fields: { billingDetails: props.contact ? { address: "never", name: "never", email: "never" } : { address: "never" } },
          ...(props.contact ? {} : { defaultValues: { billingDetails: { name, email } } }),
        });
        mounted = [payment];
        payment.on("ready", () => alive && setPhase("ready"));
        payment.on("loaderror", () => fail(null));
        payment.on("change", (e) => {
          if (!alive) return;
          onComplete.current?.(!!e.complete);
          onMethod.current?.(e.value?.type ?? null);
        });
        if (!payRef.current) return;
        payment.mount(payRef.current);
        onHandle.current({
          confirm: async (mark) => {
            const checked = await elements.submit();
            mark?.("submit");
            if (checked.error) return { ok: false, message: checked.error.message ?? TRY_AGAIN };
            const address = current.current;
            if (!address) return { ok: false, message: "Add the billing address." };
            const { error, setupIntent } = await stripe.confirmSetup({
              elements,
              redirect: "if_required",
              confirmParams: { return_url: `${location.origin}${RETURN_PATH}`, payment_method_data: { billing_details: { address, ...(props.contact ? props.contact() : {}) } } },
            });
            mark?.("confirm");
            if (error || !setupIntent) {
              // The fields show their own errors; a decline says what happened in Stripe's plain words.
              const said = error && (error.type === "card_error" || error.type === "validation_error") ? error.message : null;
              return { ok: false, message: said ?? TRY_AGAIN };
            }
            return { ok: true, setupIntentId: setupIntent.id };
          },
        });
      } catch (e) {
        fail(e instanceof ApiError ? e.message : null);
      }
    })();
    return () => {
      alive = false;
      elementsRef.current = null;
      onHandle.current(null);
      onComplete.current?.(false);
      for (const el of mounted) el.destroy();
    };
  }, [props.purpose, attempt]);

  // The address fields, once they're wanted (no address known, or Edit) and the payment fields are on their way.
  useEffect(() => {
    const elements = elementsRef.current;
    if (!editing || !elements || phase === "starting" || phase === "failed" || !addrRef.current) return;
    const address = elements.create("address", {
      mode: "billing",
      allowedCountries: ["US"],
      defaultValues: { name: props.company ?? tenant.value?.name ?? "", address: known ? toStripe(known) : { country: "US" } },
      display: { name: "organization" },
    });
    address.on("change", (e) => {
      const done = e.complete && e.value?.address ? e.value.address : null;
      current.current = done;
      onAddress.current?.(done ? toAddress(done) : null);
    });
    address.mount(addrRef.current);
    return () => {
      address.destroy();
      current.current = known ? toStripe(known) : null;
      onAddress.current?.(null);
    };
  }, [editing, phase === "starting" || phase === "failed"]);

  if (phase === "failed") {
    return (
      <div class="bill-form is-failed" role="alert">
        <p class="t-body">{failure ?? "The payment form didn't load."}</p>
        <Button variant="secondary" icon="undo" onClick={() => setAttempt((n) => n + 1)}>
          Try Again
        </Button>
      </div>
    );
  }
  return (
    <div class="bill-form" aria-busy={phase === "ready" ? undefined : "true"}>
      {phase === "starting" ? <Skeleton class="bill-form-wait" lines={6} /> : null}
      <div class="bill-pay" ref={payRef} />
      {phase === "starting" ? null : editing ? (
        <>
          <h4 class="bill-sub">Billing address</h4>
          <div class="bill-addr" ref={addrRef} />
        </>
      ) : (
        <div class="bill-addr-line">
          <span class="bill-k">Billing address</span>
          <span class="bill-addr-text">{known ? addressLine(known) : ""}</span>
          <LinkButton onClick={() => setEditing(true)}>Edit</LinkButton>
        </div>
      )}
    </div>
  );
}
